import {
  faBan,
  faFloppyDisk,
  faPen,
  faXmark
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { isEmpty } from 'lodash';
import { useMemo, useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import LargeCard from '@/components/LargeCard';
import LoadingIndicator from '@/components/LoadingIndicator';
import { PERMISSIONS } from '@/constants/auth.constants';
import { AI_ANALYST_NAME } from '@/constants/common';
import useConfirm from '@/hooks/useConfirm';
import { isValidEmail } from '@/utils/validation.utils';

import {
  AI_RESPONSE_MODE_OPTIONS,
  AI_RESPONSE_PRIORITIES
} from './AIResponseActionMatrix.constants';
import { useFetchAIResponseActions } from './AIResponseActionMatrix.store';
import useStyles from './AIResponseActionMatrix.styles';
import {
  type AIResponseActionConfig,
  type AIResponseActionDTO,
  type AIResponseActionKeys,
  type AIResponseActionMatrixProps,
  AIResponseActionModeEnum,
  type AIResponseActionPriority,
  type HostIsolationExclusions as HostIsolationExclusionsValue
} from './AIResponseActionMatrix.types';
import {
  AIResponseBuildConfig,
  normalizeAndValidateHostNames
} from './AIResponseActionMatrix.utils';
import HostIsolationExclusions, {
  HostIsolationExclusionsHeading
} from './HostIsolationExclusions';

const toExcludedUsersString = (users: string[] | null | undefined) =>
  (users ?? []).join(', ');

const DEFAULT_HOST_EXCLUSIONS = {
  excluded_host_names: [],
  excluded_asset_types: [],
  excluded_asset_subtypes: [],
  excluded_asset_tags: []
};

const hasHostIsolationExclusions = (exclusions: HostIsolationExclusionsValue) =>
  exclusions.excluded_host_names.length > 0 ||
  exclusions.excluded_asset_types.length > 0 ||
  exclusions.excluded_asset_subtypes.length > 0 ||
  exclusions.excluded_asset_tags.length > 0;

const AIResponseActionMatrix = ({
  handleSave,
  isSaving,
  isSettingsError,
  orgInfo,
  organizationLabel,
  retrySettings
}: AIResponseActionMatrixProps) => {
  const { classes } = useStyles();
  const [editing, setEditing] = useState(false);
  const { data: actions = [], isLoading } = useFetchAIResponseActions();
  const [excludedUsersError, setExcludedUsersError] = useState<string | null>(
    null
  );
  const [hostError, setHostError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const {
    ConfirmationModal: AddHostIsolationExclusionsConfirmation,
    confirm: confirmAddHostIsolationExclusions
  } = useConfirm({
    title: 'Confirm host-isolation exclusions',
    message:
      "Adding any exclusions restricts host isolations: they will only be executed when there is a matching asset for that host in your organization's Asset Inventory. Hosts unknown to Pondurance will be skipped from host isolation. To allow host isolation to execute on unknown hosts, remove all exclusions."
  });
  const {
    ConfirmationModal: RemoveHostIsolationExclusionsConfirmation,
    confirm: confirmRemoveHostIsolationExclusions
  } = useConfirm({
    title: 'Confirm host-isolation exclusions',
    message:
      'Removing all exclusions allows host isolation to execute on hosts unknown to Pondurance when the supporting response actions setting is enabled.'
  });
  const savedHostExclusions =
    orgInfo?.host_isolation_exclusions ?? DEFAULT_HOST_EXCLUSIONS;
  const initialConfig = useMemo(
    () =>
      AIResponseBuildConfig(
        actions,
        (orgInfo?.ai_response_action_config ?? {}) as AIResponseActionConfig
      ),

    [actions, orgInfo?.ai_response_action_config]
  );

  // Local edits (and the optimistic saved excluded users) layered over the
  // server values. They are discarded whenever the server values change, so a
  // parent update replaces them and an old draft cannot resurface later.
  const serverExcludedUsers = orgInfo?.response_actions_excluded_users;
  const serverExcludedUsersString = toExcludedUsersString(serverExcludedUsers);
  const serverState = {
    baseConfig: initialConfig,
    baseUsers: serverExcludedUsers,
    baseHostExclusions: savedHostExclusions,
    baseOrganizationLabel: organizationLabel,
    config: initialConfig,
    excludedUsers: serverExcludedUsersString,
    originalExcludedUsers: serverExcludedUsersString,
    hostExclusions: savedHostExclusions
  };
  const [local, setLocal] = useState<typeof serverState | null>(null);
  const isLocalStale =
    local !== null &&
    (local.baseConfig !== initialConfig ||
      local.baseUsers !== serverExcludedUsers ||
      local.baseHostExclusions !== savedHostExclusions ||
      local.baseOrganizationLabel !== organizationLabel);
  if (isLocalStale) {
    setLocal(null);
  }
  const { config, excludedUsers } =
    local !== null && !isLocalStale ? local : serverState;
  const hostExclusions =
    local !== null && !isLocalStale
      ? local.hostExclusions
      : serverState.hostExclusions;
  const originalConfig = initialConfig;

  const updateLocal = (
    update: (current: typeof serverState) => Partial<typeof serverState>
  ) =>
    setLocal((prev) => {
      const current = prev ?? serverState;
      return { ...current, ...update(current) };
    });

  const isDirty = (
    actionKey: AIResponseActionKeys,
    priority: AIResponseActionPriority
  ): boolean =>
    (config?.[actionKey]?.[priority] ?? AIResponseActionModeEnum.DISABLED) !==
    (originalConfig?.[actionKey]?.[priority] ??
      AIResponseActionModeEnum.DISABLED);

  const handleModeChange = (
    actionKey: string,
    priority: string,
    value: AIResponseActionModeEnum
  ) => {
    updateLocal((current) => ({
      config: {
        ...current.config,
        [actionKey as AIResponseActionKeys]: {
          ...current.config[actionKey as AIResponseActionKeys],
          [priority]: value
        }
      }
    }));
  };

  const saveSettings = (
    payload: Parameters<AIResponseActionMatrixProps['handleSave']>[0]
  ) => {
    void handleSave(payload)
      .then(() => {
        setEditing(false);
        setLocal(null);
        setSaveError(null);
      })
      .catch(() => {
        setSaveError('Settings were not saved. Review the draft and retry.');
      });
  };

  const handleSaveClick = async () => {
    if (isSaving) return;
    const excludedUsersArray = excludedUsers
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    const invalid = excludedUsersArray.filter((e) => !isValidEmail(e));
    if (invalid.length > 0) {
      setExcludedUsersError(`Invalid email address(es): ${invalid.join(', ')}`);
      return;
    }
    const hosts = normalizeAndValidateHostNames(
      hostExclusions.excluded_host_names
    );
    if (!hosts.ok) {
      setHostError(hosts.error);
      return;
    }
    setExcludedUsersError(null);
    const normalizedHostExclusions = {
      ...hostExclusions,
      excluded_host_names: hosts.values
    };
    const payload = {
      ai_response_action_config: config,
      response_actions_excluded_users: excludedUsersArray,
      host_isolation_exclusions: normalizedHostExclusions
    };
    if (
      hasHostIsolationExclusions(normalizedHostExclusions) !==
      hasHostIsolationExclusions(savedHostExclusions)
    ) {
      const confirmed = await (hasHostIsolationExclusions(
        normalizedHostExclusions
      )
        ? confirmAddHostIsolationExclusions()
        : confirmRemoveHostIsolationExclusions());
      if (!confirmed) return;
    }
    saveSettings(payload);
  };

  const handleCancelClick = () => {
    setEditing(false);
    setLocal(null);
    setExcludedUsersError(null);
    setHostError(null);
    setSaveError(null);
  };

  if (!orgInfo && isSettingsError) {
    return (
      <LargeCard title="Response Action Automation">
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={retrySettings}>
              Retry
            </Button>
          }
        >
          Organization settings could not be loaded. Saved host-isolation policy
          is unavailable until settings load successfully.
        </Alert>
      </LargeCard>
    );
  }

  if (!orgInfo || isLoading || isEmpty(initialConfig)) {
    return <LoadingIndicator isLoading />;
  }

  if (!orgInfo.ai_enable_submission) {
    return (
      <>
        <Alert severity="info">
          AI response action automation is currently disabled. To enable, toggle
          on the &quot;Enable AI Submission&quot; setting.
        </Alert>
        <LargeCard
          title={
            <HostIsolationExclusionsHeading
              exclusions={hostExclusions}
              savedExclusions={savedHostExclusions}
            />
          }
        />
      </>
    );
  }

  return (
    <LargeCard
      title="Response Action Automation"
      rightContent={
        <CheckPermissions permissions={PERMISSIONS.ADMIN_UPDATE}>
          {editing ? (
            <Stack direction="row">
              <IconButton
                disabled={isSaving}
                onClick={handleSaveClick}
                aria-label="Save"
              >
                <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
              </IconButton>
              <IconButton
                disabled={isSaving}
                onClick={handleCancelClick}
                aria-label="Cancel"
              >
                <FontAwesomeIcon size="xs" icon={faXmark} />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={() => setEditing(true)} aria-label="Edit">
              <FontAwesomeIcon size="xs" icon={faPen} />
            </IconButton>
          )}
        </CheckPermissions>
      }
    >
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Configure Response Actions per Case Priority
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        For each response action, choose whether the {AI_ANALYST_NAME} should
        execute it after its analysis (Agentic), allow users manual execution
        (Manual), or prevent execution of the action (Disabled).
      </Typography>

      <Box className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Response Action</TableCell>
              {AI_RESPONSE_PRIORITIES.map((p) => (
                <TableCell key={p} align="center">
                  {p}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {actions.map((action: AIResponseActionDTO) => (
              <TableRow key={action.key}>
                <TableCell className={classes.actionLabelCell}>
                  <Typography sx={{ fontWeight: 'bold' }}>
                    {action.label}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {action.description}
                  </Typography>
                </TableCell>
                {AI_RESPONSE_PRIORITIES.map((priority) => {
                  const currentMode =
                    (config?.[action.key as AIResponseActionKeys]?.[
                      priority
                    ] as AIResponseActionModeEnum) ?? '--';
                  const modeOption = AI_RESPONSE_MODE_OPTIONS.find(
                    (m) => m.value === currentMode
                  );
                  return (
                    <TableCell
                      key={priority}
                      align="center"
                      className={`${classes.modeCell} ${
                        isDirty(action.key as AIResponseActionKeys, priority)
                          ? classes.modeCellDirty
                          : ''
                      }`}
                    >
                      {editing ? (
                        <Select
                          size="small"
                          value={currentMode}
                          sx={{ minWidth: '9rem' }}
                          onChange={(e: SelectChangeEvent) =>
                            handleModeChange(
                              action.key,
                              priority,
                              e.target.value as AIResponseActionModeEnum
                            )
                          }
                        >
                          {AI_RESPONSE_MODE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Stack
                                direction="row"
                                sx={{ alignItems: 'center', gap: 1 }}
                              >
                                <FontAwesomeIcon icon={option.icon} />
                                {option.label}
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Stack
                          direction="row"
                          sx={{
                            alignItems: 'center',
                            gap: 0.5,
                            justifyContent: 'center'
                          }}
                        >
                          <FontAwesomeIcon
                            icon={modeOption?.icon ?? faBan}
                            style={{ opacity: '.5' }}
                          />
                          {modeOption?.label ?? currentMode}
                        </Stack>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          Excluded Users
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={1}
          disabled={!editing}
          value={excludedUsers}
          onChange={(e) => {
            updateLocal(() => ({ excludedUsers: e.target.value }));
            if (excludedUsersError) setExcludedUsersError(null);
          }}
          placeholder="user@example.com, another@example.com"
          error={!!excludedUsersError}
          helperText={
            excludedUsersError ??
            'Comma-separated list of email addresses that will be excluded from identity response actions.'
          }
          sx={{
            '& textarea': {
              maxHeight: '12rem',
              overflowY: 'auto !important'
            }
          }}
        />
      </Box>

      <HostIsolationExclusions
        disabled={!editing || isSaving}
        exclusions={hostExclusions}
        savedExclusions={savedHostExclusions}
        organizationTags={orgInfo.tags ?? []}
        organizationLabel={organizationLabel}
        hostError={hostError}
        onHostErrorChange={setHostError}
        onChange={(exclusions) =>
          updateLocal(() => ({ hostExclusions: exclusions }))
        }
      />
      {saveError && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              disabled={isSaving}
              onClick={handleSaveClick}
            >
              Retry save
            </Button>
          }
        >
          {saveError}
        </Alert>
      )}
      <AddHostIsolationExclusionsConfirmation />
      <RemoveHostIsolationExclusionsConfirmation />
    </LargeCard>
  );
};

export default AIResponseActionMatrix;
